import NProgress from 'nprogress'
import 'nprogress/nprogress.css'
import type { Router } from 'vue-router'
import store from '@/store'
import { getSimulated } from '@/utils/simulated'
import { clearLoginCallbackUrl, getLoginEntryUrl, isLocalDebugDomain, loadLoginCallbackPath, resolveAuthRedirectUrl, resolveLoginCallbackNavigation, saveLoginCallbackUrl } from '@/utils/auth-redirect'

function resolvePostLoginRedirect(router: Router, currentPath: string) {
  const callbackPath = loadLoginCallbackPath()
  const { redirectTarget, shouldClear } = resolveLoginCallbackNavigation(currentPath, callbackPath)
  if (shouldClear) {
    clearLoginCallbackUrl()
  }
  if (!redirectTarget) {
    return null
  }

  const resolvedRoute = router.resolve(redirectTarget)
  return {
    path: resolvedRoute.path,
    query: resolvedRoute.query,
    hash: resolvedRoute.hash,
    replace: true,
  }
}

export default function verifyLoginGuard(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    NProgress.start()
    const { loadUser, logoutCallBack } = store.authStore
    const isLoginReady = () => store.authStore.loginStatus === 2
    const redirectToLoginEntry = (authContent?: unknown) => {
      const authRedirectUrl = resolveAuthRedirectUrl(authContent)
      saveLoginCallbackUrl()
      logoutCallBack()
      next(false)
      window.location.replace(authRedirectUrl ?? getLoginEntryUrl())
    }
    try {
      const requiresAuth = to.matched.some(route => route.meta?.requiresAuth)
      if (!requiresAuth) {
        next()
        return
      }

      if (isLoginReady()) {
        const redirectTarget = resolvePostLoginRedirect(router, to.fullPath)
        if (redirectTarget) {
          next(redirectTarget)
          return
        }
        next()
        return
      }
      if (getSimulated()) {
        next()
        return
      }
      await loadUser()
      // 重新从 store 实例读取登录态，避免 TS 受前置分支缩窄影响，
      // 同时也明确表达“以 loadUser 完成后的最新状态为准”。
      if (isLoginReady()) {
        const redirectTarget = resolvePostLoginRedirect(router, to.fullPath)
        if (redirectTarget) {
          next(redirectTarget)
          return
        }
        next()
        return
      }
      if (isLocalDebugDomain()) {
        next()
        return
      }
      redirectToLoginEntry()
    }
    catch (error) {
      console.error('verifyLoginGuard loadUser error', error)
      if (isLocalDebugDomain()) {
        next()
        return
      }
      redirectToLoginEntry((error as { data?: { content?: unknown } } | null)?.data?.content)
    }
    finally {
      NProgress.done()
    }
  })
}
